import { useReducer } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Skeleton, Stack } from '@citibox-panel/ui';
import type { ConfirmLockSequenceInput, LockCodes } from '@citibox-panel/api-client';
import { buildLockCodeWindow, type LockCodeSlotCounts } from '../lib/lockCodeWindow';
import { lockSequenceTarget } from '../lib/lockSequenceTarget';
import {
  lockCodeSelection,
  initialLockCodeSelection,
  type LockCodeRailKey,
} from '../model/lockCodeSelection';
import { deliveryActionFlow, initialDeliveryActionState } from '../model/deliveryActionFlow';
import { useConfirmLockSequence } from '../hooks/useConfirmLockSequence';
import { useAutoDismiss } from '../hooks/useAutoDismiss';
import { ConfirmSequenceControl } from './ConfirmSequenceControl';
import { LockCodeRailPanel } from './LockCodeRailPanel';

// The strip the design settled on: the last code given, and the next one to give. One slot each —
// the sequence slides through them, they never move. The counts are a parameter of the window
// builder precisely so this line is the only place the design fixes them.
const SLOTS: LockCodeSlotCounts = { given: 1, future: 0 };

// TODO: i18n — copy en ES hasta Fase 1.
const RAILS: readonly { key: LockCodeRailKey; id: string; title: string }[] = [
  { key: 'open', id: 'lock-codes-open', title: 'Códigos de apertura (4 dígitos)' },
  { key: 'reset', id: 'lock-codes-reset', title: 'Códigos de reset (8 dígitos)' },
];

export interface LockCodeRailsProps {
  codes: LockCodes | undefined;
  isPending: boolean;
  isFetching: boolean;
  isError: boolean;
  refetch: () => void;
  lockId: string;
  // Travels to the WRITE's metric only — the pointer belongs to the cerradura, not to the «Entrega».
  deliveryId: string;
  confirmLockSequence: (lockId: string, input: ConfirmLockSequenceInput) => Promise<void>;
}

// The two carriles, the agent's whole interaction with them, AND the read's own states. It owns the
// query branch rather than being swapped out for one, and that placement is load-bearing in two
// directions:
//
//   ABOVE the skeleton — a confirm invalidates the codes, which sets `isFetching`, so a component
//   sitting below that branch would unmount at the exact moment of success, taking its flow state
//   (and the carril the agent has open) with it.
//
//   BELOW the `open` gate — `LockCodesSection` returns null when the log disclosure is folded, so
//   this unmounts with it and the reducers die. That is what makes «closing discards the proposal»
//   structural rather than a rule someone has to remember.
export function LockCodeRails({
  codes,
  isPending,
  isFetching,
  isError,
  refetch,
  lockId,
  deliveryId,
  confirmLockSequence,
}: LockCodeRailsProps) {
  const [selection, dispatch] = useReducer(lockCodeSelection, initialLockCodeSelection);
  // The slice's THIRD instance of the shared flow, and the only one serving two controls: exactly
  // one carril is ever open, so one slot is enough — provided it is cleared when the open one
  // changes, which `onToggle` below does.
  const [flow, flowDispatch] = useReducer(deliveryActionFlow, initialDeliveryActionState);
  const queryClient = useQueryClient();
  const mutation = useConfirmLockSequence(lockId, confirmLockSequence);
  const pending = flow.phase === 'pending';

  // Gated on the banner being ON SCREEN, not merely on the phase: the settle path re-reads the
  // codes, and this component shows a skeleton while that is in flight. Starting the countdown
  // behind it would spend it on a banner nobody saw. SUCCESS only: a failure no longer waits out a
  // timer here — it floats (ADR 0021) and the control hands the flow straight back.
  useAutoDismiss(flow.phase === 'success' && !isFetching, flowDispatch);

  // `isFetching` and not just `isPending`: a re-unfold arrives with cached codes and a re-read
  // already in flight, and repainting the old sequence during it is how the agent ends up reading
  // out a code the server has since spent. The box log beside this one makes the opposite trade —
  // its cached rows are inert, these are credentials.
  if (isPending || isFetching) {
    return (
      <Stack spacing={1.5}>
        {RAILS.map(({ key }) => (
          <Skeleton key={key} width="100%" height={48} />
        ))}
      </Stack>
    );
  }

  if (isError) {
    return (
      <Box>
        {/* TODO: i18n */}
        <Alert severity="error">No se han podido cargar los códigos.</Alert>
        <Box sx={{ mt: 1 }}>
          <Button variant="text" onClick={refetch}>
            Reintentar
          </Button>
        </Box>
      </Box>
    );
  }

  if (codes === undefined) return null;

  const onConfirm = () => {
    const rail = selection === null ? null : codes[selection.rail];
    const target = lockSequenceTarget(
      rail,
      selection === null ? null : buildLockCodeWindow(rail, selection.offset, SLOTS),
    );
    // Unreachable: the control only exists when both are resolved. Here so the flow cannot hang in
    // `pending` if that ever stops holding.
    if (selection === null || target === null) return;
    flowDispatch({ type: 'SUBMIT' });
    mutation.mutate(
      { type: selection.rail, deliveryId, ...target },
      {
        onSuccess: () => {
          flowDispatch({ type: 'SUCCESS' });
          // The server pointer IS the target now, so a surviving offset would repaint the strip
          // slid all over again — from the NEW pointer. RESET keeps the carril unfolded.
          dispatch({ type: 'RESET' });
        },
        onError: () => {
          flowDispatch({ type: 'ERROR', code: null });
          // Code-agnostic, and the caller's job rather than the hook's: the report and the re-read
          // are one policy, and splitting them across two files is how they drift.
          void queryClient.invalidateQueries({ queryKey: ['lock-codes', lockId] });
        },
      },
    );
  };

  return (
    <Stack spacing={1.5}>
      {RAILS.map(({ key, id, title }) => {
        // A carril that is not the selected one is always painted at the server pointer: there is
        // only one offset in play, and it belongs to whichever carril is unfolded.
        const isSelected = selection !== null && selection.rail === key;
        const codeWindow = buildLockCodeWindow(
          codes[key],
          isSelected ? selection.offset : 0,
          SLOTS,
        );
        // No big slot, nothing to confirm — an empty carril can never produce a write.
        const nextCode = isSelected ? (codeWindow?.next?.code ?? null) : null;
        return (
          <LockCodeRailPanel
            key={key}
            id={id}
            title={title}
            expanded={isSelected}
            disabled={pending}
            onToggle={() => {
              // One flow slot, two carriles. Without this, a confirm step composed on one carril
              // re-renders against the other and a single click writes a position the agent never
              // composed, on the wrong code table. The reducer's guards make exactly one of these
              // land; `pending` is unreachable here because the header is disabled.
              flowDispatch({ type: 'CANCEL' });
              flowDispatch({ type: 'DISMISS' });
              dispatch({ type: 'TOGGLE', rail: key });
            }}
            codeWindow={codeWindow}
            onSlide={(delta) => {
              dispatch({ type: 'SLIDE', delta });
            }}
            onDiscard={() => {
              dispatch({ type: 'RESET' });
            }}
            {...(nextCode === null
              ? {}
              : {
                  confirmControl: (
                    <ConfirmSequenceControl
                      state={flow}
                      nextCode={nextCode}
                      onStart={() => {
                        flowDispatch({ type: 'START', action: 'confirmSequence' });
                      }}
                      onCancel={() => {
                        flowDispatch({ type: 'CANCEL' });
                      }}
                      onConfirm={onConfirm}
                      onFailureReported={() => {
                        flowDispatch({ type: 'DISMISS' });
                      }}
                    />
                  ),
                })}
          />
        );
      })}
    </Stack>
  );
}
